"use client";

/**
 * Cromo de la app (rediseño 2026): cabeceras y barra de navegación inferior.
 *
 * El marco de la app lo pone `app/(app)/layout.tsx` (columna centrada de
 * max 430 px). Cabecera y barra van `fixed` centradas con el mismo ancho.
 * Las páginas compensan con padding: `pt-[119px]` (logo) / `pt-[100px]`
 * (título) y `pb-[62px]` cuando muestran la barra.
 */

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";

import { useApp } from "./proveedor";
import {
  IconoAtras,
  IconoBoot,
  IconoCarrito,
  IconoCasa,
  IconoChispa,
  IconoMenu,
} from "./iconos";

/** Contenedor fijo centrado con el ancho del marco de la app. */
function Fija({
  posicion,
  children,
  className = "",
}: {
  posicion: "top" | "bottom";
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`fixed ${posicion === "top" ? "top-0" : "bottom-0"} left-1/2 z-20 w-full max-w-[430px] -translate-x-1/2 ${className}`}
    >
      {children}
    </div>
  );
}

/** Botón del carrito con contador (esquina superior derecha). */
export function BotonCarrito({ claro = false }: { claro?: boolean }) {
  const { totalItems } = useApp();
  const borde = claro ? "border-white" : "border-black";
  const trazo = claro ? "#fff" : "#040404";
  return (
    <Link
      href="/pedido"
      aria-label="Mi pedido"
      className="relative block h-[41px] w-[41px]"
    >
      <span
        className={`absolute top-2 left-0 flex h-[33px] w-9 items-center justify-center rounded-[10px] border ${borde}`}
      >
        <IconoCarrito color={trazo} />
      </span>
      {totalItems > 0 && (
        <span className="absolute top-0 left-[22px] flex h-[19px] w-[19px] items-center justify-center rounded-full bg-lima text-sm font-black text-black">
          {totalItems}
        </span>
      )}
    </Link>
  );
}

/** Cabecera con el logo centrado y el carrito (inicio, catálogo, favoritos…). */
export function HeaderLogo() {
  return (
    <Fija posicion="top">
      <div className="relative h-[119px] bg-white">
        <Link href="/inicio" aria-label="Frescazo — inicio">
          <Image
            src="/brand/logo.png"
            alt="frescazo"
            width={171}
            height={52}
            priority
            className="absolute top-[51px] left-1/2 h-[52px] w-[171px] -translate-x-1/2 object-contain"
          />
        </Link>
        <div className="absolute top-[34px] right-[28px]">
          <BotonCarrito />
        </div>
      </div>
    </Fija>
  );
}

/** Cabecera con botón atrás + título (pantallas internas). */
export function HeaderTitulo({
  titulo,
  href,
  onAtras,
}: {
  titulo: string;
  /** Destino del botón atrás; sin `href` usa el historial. */
  href?: string;
  /** Manejador propio del botón atrás (gana sobre `href`). */
  onAtras?: () => void;
}) {
  const router = useRouter();
  return (
    <Fija posicion="top">
      <div className="flex h-[100px] items-end gap-3.5 border-b border-black/5 bg-white px-6 pb-[18px]">
        <button
          type="button"
          aria-label="Volver"
          onClick={() =>
            onAtras ? onAtras() : href ? router.push(href) : router.back()
          }
          className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-xl bg-[#f9f9f9]"
        >
          <IconoAtras color="#221F1F" />
        </button>
        <span className="pb-[9px] font-app text-xl leading-5 font-semibold text-black">
          {titulo}
        </span>
      </div>
    </Fija>
  );
}

const PESTANAS = [
  { href: "/menu", label: "Menu", icono: "menu" },
  { href: "/inicio", label: "Inicio", icono: "casa" },
  { href: "/productos", label: "Productos", icono: "boot" },
  { href: "/pepa", label: "A mi medida", icono: "chispa" },
] as const;

/** Rutas que "pertenecen" a cada pestaña para pintar el punto activo. */
function pestanaActiva(pathname: string): string {
  if (pathname.startsWith("/menu")) return "/menu";
  if (pathname === "/inicio") return "/inicio";
  if (
    pathname.startsWith("/productos") ||
    pathname.startsWith("/buscar") ||
    pathname.startsWith("/favoritos")
  )
    return "/productos";
  if (pathname.startsWith("/pepa")) return "/pepa";
  return "";
}

/** Barra de navegación inferior de 4 pestañas con punto verde activo. */
export function BottomNav() {
  const pathname = usePathname();
  const activa = pestanaActiva(pathname);

  return (
    <Fija posicion="bottom">
      <nav className="flex h-[62px] items-start justify-around bg-white pt-3 shadow-[0_-1px_0_rgba(0,0,0,.06)]">
        {PESTANAS.map((p) => (
          <Link
            key={p.href}
            href={p.href}
            className="flex flex-col items-center gap-px text-black"
          >
            <span className="flex h-5 items-center justify-center">
              {p.icono === "menu" && <IconoMenu color="#000" />}
              {p.icono === "casa" && <IconoCasa color="#000" />}
              {p.icono === "boot" && <IconoBoot color="#000" />}
              {p.icono === "chispa" && <IconoChispa color="#000" />}
            </span>
            <span className="text-sm leading-5">{p.label}</span>
            <span
              className={`h-[5px] w-[5px] rounded-full ${activa === p.href ? "bg-verde" : "bg-transparent"}`}
            />
          </Link>
        ))}
      </nav>
    </Fija>
  );
}

/** Botón principal del rediseño (pill grande). */
export function BotonPrimario({
  children,
  onClick,
  variante = "verde",
  className = "",
  disabled = false,
  type = "button",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variante?: "verde" | "lima" | "bosque" | "blanco";
  className?: string;
  disabled?: boolean;
  type?: "button" | "submit";
}) {
  const estilos: Record<string, string> = {
    verde: "bg-verde text-white",
    lima: "bg-lima text-bosque",
    bosque: "bg-bosque text-white",
    blanco: "bg-white text-bosque",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`flex h-16 w-full items-center justify-center gap-2.5 rounded-[30px] font-app text-[17px] font-semibold transition active:scale-[.98] disabled:opacity-60 ${estilos[variante]} ${className}`}
    >
      {children}
    </button>
  );
}
